import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Building, Package, GaugeCircle, RefreshCw } from "lucide-react";
import "./Analytics2.css";

const NCLARITY_TELEMETRY_QUERY = "https://api.nclarity.com/v3/telemetry/query";
const NCLARITY_EQUIPMENT = "https://api.nclarity.com/v3/equipment";

const FIELD_MAP = {
  callForCool: "Call for Cool",
  callForHeat: "Call for Heat",
  "sadb-1": "Supply Dry Bulb",
  "radb-1": "Return Dry Bulb",
  wst: "Chiller Water Supply",
  wrt: "Chiller Water Return",
  "amps-1": "Current 1",
  "amps-2": "Current 2",
  "amps-3": "Current 3",
};

const Analytics2 = ({ nclarityBuildings = [] }) => {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [buildings, setBuildings] = useState([]);
  const [equipment, setEquipment] = useState([]);

  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [selectedBuilding, setSelectedBuilding] = useState(null);

  const [equipmentDevices, setEquipmentDevices] = useState({});
  const [deviceTelemetry, setDeviceTelemetry] = useState({});
  const [telemetryLoading, setTelemetryLoading] = useState(false);
  const [telemetryError, setTelemetryError] = useState("");

  // NEW: view-mode for right pane
  const [showAllDevices, setShowAllDevices] = useState(false);

  // ----- Helpers ------------------------------------------------------------

  // Equipment now comes straight from the nClarity API (the deprecated
  // nclarity.public.equipment Snowflake mirror is no longer used). The endpoint
  // returns every equipment record for the account in one payload, so we fetch
  // once and filter by building below.
  const fetchNClarityEquipment = async () => {
    const headers = getNClarityHeaders();
    try {
      const resp = await axios.get(`${NCLARITY_EQUIPMENT}?limit=1000`, {
        headers,
      });
      const list = resp?.data?.data || [];
      return list.map((e) => ({
        BUILDING_ID: e.buildingId,
        BUILDING_NAME: e.building?.name || "",
        EQUIPMENT_ID: e.id,
        EQUIPMENT_TYPE: e.type || "",
        EQUIPMENT_NAME:
          (e.equipmentProfile?.data?.equipmentInfo?.System?.Name || "").trim() ||
          e.pulseId ||
          e.id,
        STATUS: e.statusRecord?.status || "",
      }));
    } catch (error) {
      console.error("❌ equipment fetch failed", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
      return [];
    }
  };

  const getNClarityHeaders = () => {
    const nclarityToken =
      localStorage.getItem("NCLARITY_API_KEY") ||
      process.env.REACT_APP_NCLARITY_API_KEY;
    if (!nclarityToken) {
      console.warn("⚠️ NCLARITY_API_KEY missing for upstream calls.");
      return {};
    }
    return { Authorization: `Bearer ${nclarityToken}` };
  };

  const fetchDevices = async () => {
    const headers = getNClarityHeaders();

    let deviceUrl = "https://api.nclarity.com/v3/device?limit=1000";
    const devices = [];
    try {
      while (deviceUrl) {
        const resp = await axios.get(deviceUrl, { headers });
        const data = resp.data || {};
        devices.push(...(data.data || []));
        deviceUrl = data?.links?.next || null;
      }
    } catch (e) {
      console.error("❌ devices fetch failed", {
        deviceUrl,
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message,
      });
    }

    // Build equipmentId → [deviceId]
    const byEquip = {};
    devices.forEach((dev) => {
      const eqId = dev?.equipment?.id;
      const dId = dev?.deviceId;
      if (!eqId || !dId) return;
      if (!byEquip[eqId]) byEquip[eqId] = [];
      if (!byEquip[eqId].includes(dId)) byEquip[eqId].push(dId);
    });

    setEquipmentDevices(byEquip);

    // merge device counts into existing equipment objects if present
    setEquipment((prev) =>
      prev.map((eq) => ({
        ...eq,
        devices: byEquip[eq.id] || [],
      }))
    );
  };

  const buildFlux = (deviceId) => {
    const fields = Object.keys(FIELD_MAP)
      .map((f) => `r._field == "${f}"`)
      .join(" or ");
    // Only return CURRENT data. The bucket holds 5-minute rollups, so a device
    // that's actually reporting will have a sample within the last 15 minutes.
    // If nothing comes back, the equipment is treated as offline (no stale
    // fallback to an hours-old reading).
    return `
  from(bucket: "devices_telemetry_5m")
    |> range(start: -15m)
    |> filter(fn: (r) => r._measurement == "telemetry")
    |> filter(fn: (r) => r.deviceId == "${deviceId}")
    |> filter(fn: (r) => ${fields})
    |> sort(columns: ["_time"])
    |> last()
  `;
  };
  const buildAllFieldsFlux = (deviceId) => `
  from(bucket: "devices_telemetry_5m")
    |> range(start: -15m)
    |> filter(fn: (r) => r._measurement == "telemetry")
    |> filter(fn: (r) => r.deviceId == "${deviceId}")
    |> last()
`;

  const fetchDeviceTelemetry = async (deviceIds = []) => {
    if (!deviceIds.length) return {};
    const headers = { ...getNClarityHeaders() };
    const results = {};

    await Promise.all(
      deviceIds.map(async (deviceId) => {
        const query = buildFlux(deviceId).trim();

        try {
          const resp = await axios.post(
            NCLARITY_TELEMETRY_QUERY,
            { query },
            { headers }
          );

          const rows = resp?.data?.data || [];
          if (!rows.length) {
            results[deviceId] = { _time: null };
            return;
          }

          // Each row is the last sample for one field, so each carries its own
          // _time. Some fields (e.g. callForCool/callForHeat) only change on an
          // event and can be hours stale while temps/amps are current — use the
          // newest timestamp across all fields as the device's latest sample.
          // Fall back to the first row's time if none of the rows expose _time.
          let latestTime = null;
          const snapshot = {};
          rows.forEach((r) => {
            if (r._field != null) snapshot[r._field] = r._value;
            if (r._time && (latestTime == null || r._time > latestTime)) {
              latestTime = r._time;
            }
          });
          snapshot._time = latestTime ?? rows[0]?._time ?? null;
          if (DEBUG_TELEMETRY) {
            console.groupCollapsed(`📟 Device snapshot ${deviceId}`);
            console.log(snapshot);
            console.groupEnd();
          }

          results[deviceId] = snapshot;
        } catch (e) {
          console.error("❌ telemetry fetch failed", {
            deviceId,
            status: e?.response?.status,
            data: e?.response?.data,
            message: e?.message,
          });
          results[deviceId] = { _time: null, error: true };
        }
      })
    );

    return results;
  };

  const formatValue = (val, key = "") => {
    if (val === null || val === undefined || val === "—" || Number.isNaN(val)) {
      return "—";
    }

    // Boolean calls in grid (if any sneak in)
    if (key === "ty" || key === "tw") {
      return val ? "ON" : "OFF";
    }

    // Temps — AIR (C→F)
    if (key && (key.startsWith("sadb") || key.startsWith("radb"))) {
      const c = Number(val);
      const f = (c * 9) / 5 + 32;
      return `${f.toFixed(2)} °F`;
    }

    // Temps — WATER (C→F) for chillers
    if (key === "wst" || key === "wrt") {
      const c = Number(val);
      const f = (c * 9) / 5 + 32;
      return `${f.toFixed(2)} °F`;
    }

    // Electrical currents → Amps
    if (key && key.startsWith("amps")) {
      return `${Number(val).toFixed(2)} A`;
    }

    // Default number formatting
    return `${Number(val).toFixed(2)}`;
  };

  // ----- UI helpers ---------------------------------------------------------
  const shouldShowMetric = (val, key = "") => {
    if (val === null || val === undefined) return false;

    // numeric-ish points must be valid numbers
    if (
      key.startsWith("sadb") ||
      key.startsWith("radb") ||
      key.startsWith("amps")
    ) {
      const n = Number(val);
      return Number.isFinite(n);
    }

    return true; // fallback for any future fields
  };

  const handleEquipmentSelect = (equipmentId) => {
    setShowAllDevices(false);
    setSelectedEquipment(equipmentId);
  };

  const handleBuildingSelect = (buildingName) => {
    setShowAllDevices(false);
    setSelectedBuilding(buildingName);
    // Optional: clear selected equipment if it no longer belongs to this building
    const eq = equipment.find((e) => e.id === selectedEquipment);
    if (eq && eq.building !== buildingName) {
      setSelectedEquipment(null);
      setDeviceTelemetry({});
    }
  };

  // Shows ALL devices for the current building filter (or all buildings if none)
  const handleViewAll = () => {
    setSelectedEquipment(null);
    setShowAllDevices(true);
    setDeviceTelemetry({});
  };

  // Full reset (buildings + equipment)
  const handleResetAll = () => {
    setSelectedEquipment(null);
    setSelectedBuilding(null);
    setShowAllDevices(true); // still show all devices across all buildings
    setDeviceTelemetry({});
  };

  const refreshTelemetry = async () => {
    const deviceIds = getDeviceIdsForCurrentView();
    if (!deviceIds.length) return;

    setTelemetryLoading(true);
    try {
      const snaps = await fetchDeviceTelemetry(deviceIds);
      setDeviceTelemetry(snaps);
    } catch (e) {
      const msg = e?.message || "Failed to reload telemetry.";
      console.error("❌ telemetry refresh failed", msg);
      setTelemetryError(msg);
    } finally {
      setTelemetryLoading(false);
    }
  };
  // Toggle this to silence logs quickly
  const DEBUG_TELEMETRY = true;

  // Pretty-print a batch of snapshots
  const debugLogTelemetry = (tag, snapsObj) => {
    if (!DEBUG_TELEMETRY) return;
    try {
      const rows = Object.entries(snapsObj).map(([deviceId, snap]) => ({
        deviceId,
        _time: snap?._time || null,
        ty: snap?.ty,
        tw: snap?.tw,
        "sadb-1": snap?.["sadb-1"],
        "radb-1": snap?.["radb-1"],
        "amps-1": snap?.["amps-1"],
        "amps-2": snap?.["amps-2"],
        "amps-3": snap?.["amps-3"],
      }));
      console.groupCollapsed(
        `🔎 Telemetry batch: ${tag} (devices=${rows.length})`
      );
      console.table(rows);
      console.groupEnd();
    } catch (e) {
      console.warn("debugLogTelemetry failed", e);
    }
  };

  // ----- Derived data -------------------------------------------------------
  // normalize
  const toBool = (v) => {
    if (v === true) return true;
    if (v === false) return false;
    if (v === 1 || v === "1" || v === "true") return true;
    if (v === 0 || v === "0" || v === "false") return false;
    return Boolean(v);
  };

  // extra helpers to infer activity if explicit "call" is missing
  const isCompressorLikelyOn = (snap) => {
    // Any of these hint cooling is active:
    // - runtimeOne (or similar) true
    // - timeInCooling > 0
    // - coolMin true
    // - totalAmps above a small threshold (tweak as needed)
    if (toBool(snap.runtimeOne)) return true;
    if (Number(snap.timeInCooling) > 0) return true;
    if (toBool(snap.coolMin)) return true;
    if (Number(snap.totalAmps) > 30) return true; // heuristic: adjust for your site
    return false;
  };

  const getCallStatus = (snap, kind) => {
    if (kind === "cool") {
      // prefer explicit RTU flag; else wires; else heuristic
      const explicit = snap.callForCool;
      const wire = snap.ty;
      if (explicit != null) return toBool(explicit);
      if (wire != null) return toBool(wire);
      return isCompressorLikelyOn(snap);
    }
    if (kind === "heat") {
      const explicit = snap.callForHeat;
      const wire = snap.tw;
      if (explicit != null) return toBool(explicit);
      if (wire != null) return toBool(wire);
      // optional: add a heat heuristic if you have a good proxy at your site
      return false;
    }
    return false;
  };

  const filteredEquipment = useMemo(
    () =>
      equipment
        .filter((eq) =>
          selectedBuilding ? eq.building === selectedBuilding : true
        )
        .sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""), undefined, {
            numeric: true,
            sensitivity: "base",
          })
        ),
    [equipment, selectedBuilding]
  );

  // choose best available temperature pair; prefer Fahrenheit if present
  const pickAirTempsF = (snap) => {
    // Prefer explicit °F if present
    if (Number.isFinite(snap.radbF) && Number.isFinite(snap.sadbF)) {
      return { returnF: Number(snap.radbF), supplyF: Number(snap.sadbF) };
    }
    // Else use C and convert
    const radbC = Number(snap["radb-1"]);
    const sadbC = Number(snap["sadb-1"]);
    if (Number.isFinite(radbC) && Number.isFinite(sadbC)) {
      const c2f = (c) => (c * 9) / 5 + 32;
      return { returnF: c2f(radbC), supplyF: c2f(sadbC) };
    }
    return null;
  };

  const computeDeltaTF = (snap) => {
    // Prefer the raw °F value if API provides deltaTf
    if (Number.isFinite(snap.deltaTf)) return Number(snap.deltaTf);

    const pair = pickAirTempsF(snap);
    if (!pair) return null;

    const dt = pair.returnF - pair.supplyF;
    // sanity clamp (optional): ignore impossible values
    if (Math.abs(dt) > 80) return null;
    return dt;
  };

  // helper to compute device ids based on current view
  const getDeviceIdsForCurrentView = () => {
    // If a specific equipment is selected, prefer that
    if (selectedEquipment) {
      return equipmentDevices[selectedEquipment] || [];
    }

    // If a building is selected (with or without "View All"), show all devices in that building
    if (selectedBuilding) {
      const ids = [];
      for (const eq of filteredEquipment) {
        const list = equipmentDevices[eq.id] || [];
        ids.push(...list);
      }
      return Array.from(new Set(ids));
    }

    // Otherwise, fall back to "View All" across all buildings (if toggled)
    if (showAllDevices) {
      const ids = [];
      for (const eq of filteredEquipment) {
        // filteredEquipment === all equipment when no building selected
        const list = equipmentDevices[eq.id] || [];
        ids.push(...list);
      }
      return Array.from(new Set(ids));
    }

    return [];
  };

  const deviceIdsToRender = getDeviceIdsForCurrentView();

  const formatTimeAgo = (iso) => {
    if (!iso) return "—";
    const now = Date.now();
    const ts = new Date(iso).getTime();
    if (isNaN(ts)) return "—";

    const diffSec = Math.floor((now - ts) / 1000);
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
  };

  // ----- Effects ------------------------------------------------------------

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!nclarityBuildings || nclarityBuildings.length === 0) {
        console.warn(
          "⚠️ No nclarityBuildings provided — skipping analytics."
        );
        setLoading(false);
        return;
      }

      setLoading(true);

      // Pull the full equipment list once, then keep only equipment that
      // belongs to the user's nClarity building(s) and is actually installed.
      // "AwaitingInstallation" units have no device/telemetry yet, so they'd
      // otherwise show up as empty "no sample" cards.
      const buildingIds = new Set(nclarityBuildings.map((b) => b.ID));
      const allEquipment = await fetchNClarityEquipment();
      const analyticsData = allEquipment.filter(
        (e) =>
          buildingIds.has(e.BUILDING_ID) && e.STATUS !== "AwaitingInstallation"
      );

      const uniqueBuildings = [
        ...new Set(analyticsData.map((row) => row.BUILDING_NAME)),
      ];

      const equipmentMap = {};
      analyticsData.forEach((row) => {
        if (!equipmentMap[row.EQUIPMENT_ID]) {
          equipmentMap[row.EQUIPMENT_ID] = {
            id: row.EQUIPMENT_ID,
            name: row.EQUIPMENT_NAME,
            type: row.EQUIPMENT_TYPE,
            building: row.BUILDING_NAME,
          };
        }
      });

      const filteredData = analyticsData.map((row) => {
        let telemetry = {};
        try {
          telemetry = row.TELEMETRY_DATA ? JSON.parse(row.TELEMETRY_DATA) : {};
        } catch {
          telemetry = {};
        }
        return { ...row, TELEMETRY_DATA: telemetry };
      });

      setRawData(filteredData);
      setBuildings(uniqueBuildings);
      setEquipment(Object.values(equipmentMap));
      setShowAllDevices(true);
      setSelectedBuilding(null);
      setSelectedEquipment(null);
      setLoading(false);
    };

    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nclarityBuildings]);

  useEffect(() => {
    if (!loading) {
      fetchDevices();
    }
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // React to changes in view (single vs all) and fetch telemetry
  useEffect(() => {
    const run = async () => {
      setTelemetryError("");
      setDeviceTelemetry({});

      const ids = getDeviceIdsForCurrentView();
      if (!ids.length) return;

      setTelemetryLoading(true);
      try {
        const snapshots = await fetchDeviceTelemetry(ids);
        setDeviceTelemetry(snapshots);
      } catch (e) {
        const msg = e?.message || "Failed to load telemetry.";
        console.error("❌ telemetry bulk failed", msg);
        setTelemetryError(msg);
      } finally {
        setTelemetryLoading(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAllDevices, selectedEquipment, selectedBuilding, equipmentDevices]);

  // ----- Render -------------------------------------------------------------

  return (
    <div className="analytics2-wrapper">
      <div className="analytics2-container">
        {/* Page header */}
        <header className="page-header">
          <div className="page-heading">
            <GaugeCircle size={24} className="heading-icon" />
            <div>
              <h1 className="dashboard-heading">Dynamiq Snapshots Dashboard</h1>
              <p className="subheading">
                Viewing insights across{" "}
                <strong>{nclarityBuildings.length}</strong>{" "}
                {nclarityBuildings.length === 1 ? "building" : "buildings"}
              </p>
            </div>
          </div>

          <div className="header-chips">
            <span className="pill neutral">
              Buildings: <strong>{buildings.length}</strong>
            </span>
            <span className="pill neutral">
              Equipment: <strong>{filteredEquipment.length}</strong>
            </span>
            {selectedEquipment && (
              <span className="pill accent">Selected Equipment</span>
            )}
            {showAllDevices && <span className="pill light">All Devices</span>}
          </div>
        </header>

        {/* Sections */}
        {loading ? (
          <div className="loader-grid">
            {[...Array(6)].map((_, i) => (
              <div className="loader-card" key={i}></div>
            ))}
          </div>
        ) : (
          <div className="layout-grid">
            {/* LEFT: selectors */}
            <aside className="sidebar-pane">
              {/* Buildings */}
              <section className="section">
                <div className="section-head tight">
                  <div className="section-title">
                    <Building size={18} />
                    <h3>Select Building</h3>
                  </div>
                </div>

                <div className="buildings-list vertical">
                  {buildings.map((building) => (
                    <button
                      key={building}
                      className={`chip block ${
                        selectedBuilding === building ? "selected" : ""
                      }`}
                      onClick={() => handleBuildingSelect(building)}
                    >
                      {building}
                    </button>
                  ))}
                  {selectedBuilding && (
                    <button
                      className="chip ghost block"
                      onClick={handleResetAll}
                    >
                      Clear Building Filter
                    </button>
                  )}
                </div>
              </section>

              {/* Equipment */}
              <section className="section">
                <div className="section-head tight">
                  <div className="section-title">
                    <Package size={18} />
                    <h3>Select Equipment</h3>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {selectedEquipment && (
                      <button
                        className="view-all-btn small"
                        onClick={handleViewAll}
                        title="Show telemetry for all devices in current filter"
                      >
                        View All
                      </button>
                    )}
                    {!selectedEquipment && !showAllDevices && (
                      <button
                        className="view-all-btn small"
                        onClick={handleViewAll}
                        title="Show telemetry for all devices in current filter"
                      >
                        View All
                      </button>
                    )}
                  </div>
                </div>

                <div className="equipment-list-rail">
                  {filteredEquipment.map((eq) => (
                    <button
                      key={eq.id}
                      className={`equipment-rail-item ${
                        eq.id === selectedEquipment ? "selected" : ""
                      }`}
                      onClick={() => handleEquipmentSelect(eq.id)}
                      title={`${eq.name || "Equipment"} • ${eq.type || "—"} • ${
                        eq.building || "—"
                      }`}
                    >
                      <div className="rail-name">{eq.name || "Unnamed"}</div>
                      <div className="rail-meta">{eq.type || "—"}</div>
                      <div className="rail-meta dim">{eq.building || "—"}</div>
                      {eq.devices?.length ? (
                        <span className="pill light tiny">
                          Devices: {eq.devices.length}
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              </section>
            </aside>

            {/* RIGHT: telemetry */}
            <main className="main-pane">
              <section className="telemetry-section card">
                <div className="telemetry-header row">
                  <div>
                    <h3>Telemetry Snapshot</h3>
                    <p className="telemetry-sub">
                      {showAllDevices
                        ? `All devices${
                            selectedBuilding ? ` • ${selectedBuilding}` : ""
                          }`
                        : selectedEquipment
                        ? `${
                            equipment.find((e) => e.id === selectedEquipment)
                              ?.name || "Selected equipment"
                          }`
                        : "Select an equipment or click View All to see telemetry."}
                    </p>
                  </div>

                  {deviceIdsToRender.length > 0 && (
                    <button
                      className="view-data-btn big"
                      onClick={refreshTelemetry}
                      disabled={telemetryLoading}
                      title="Refresh telemetry"
                    >
                      <RefreshCw
                        size={16}
                        style={{ marginRight: 6, verticalAlign: "middle" }}
                      />
                      {telemetryLoading ? "Refreshing…" : "Refresh"}
                    </button>
                  )}
                </div>

                {deviceIdsToRender.length === 0 ? (
                  <div className="empty-state">
                    <p>
                      {selectedEquipment
                        ? "No devices found for this equipment."
                        : "Choose a building/equipment or use View All to see telemetry."}
                    </p>
                  </div>
                ) : telemetryError ? (
                  <div className="empty-state error">
                    <span className="empty-dot" />
                    <p>{telemetryError}</p>
                  </div>
                ) : (
                  <div className="device-telemetry-list vertical">
                    {deviceIdsToRender.map((deviceId) => {
                      const snap = deviceTelemetry[deviceId] || {};
                      const ts = snap?._time
                        ? new Date(snap._time).toLocaleString()
                        : "—";

                      const coolOn = getCallStatus(snap, "cool");
                      const heatOn = getCallStatus(snap, "heat");
                      // No current sample (within the query window) ⇒ offline.
                      const online = !!snap?._time;

                      return (
                        <div
                          className={`device-card pretty wide ${
                            online ? "" : "offline"
                          }`}
                          key={deviceId}
                        >
                          {telemetryLoading && (
                            <div className="loading-overlay">
                              <div className="spinner" />
                            </div>
                          )}

                          <div className="device-card-top">
                            <div className="device-id">
                              <span className="device-label">Device</span>
                              <span className="device-value">{deviceId}</span>
                            </div>

                            <div className="call-badges">
                              {online ? (
                                <>
                                  <span
                                    className={`call-badge cool ${
                                      coolOn ? "on" : "off"
                                    }`}
                                  >
                                    <span className="dot" />
                                    Call for Cool
                                    <span>• {coolOn ? "ON" : "OFF"}</span>
                                  </span>
                                  <span
                                    className={`call-badge heat ${
                                      heatOn ? "on" : "off"
                                    }`}
                                  >
                                    <span className="dot" />
                                    Call for Heat
                                    <span>• {heatOn ? "ON" : "OFF"}</span>
                                  </span>
                                </>
                              ) : (
                                <span className="call-badge off">
                                  <span className="dot" />
                                  Offline
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="device-submeta">
                            <div className="last-updated">
                              {online
                                ? `Latest sample: ${formatTimeAgo(snap._time)}`
                                : "Offline — no current data"}
                            </div>
                          </div>

                          <div className="device-metrics-grid wide">
                            {Object.entries(FIELD_MAP)
                              // skip the call badges (they’re handled separately above)
                              .filter(
                                ([key]) =>
                                  key !== "callForCool" && key !== "callForHeat"
                              )
                              .filter(([key]) =>
                                shouldShowMetric(snap[key], key)
                              )
                              .map(([key, label]) => (
                                <div className="metric-chip" key={key}>
                                  <div className="metric-label">{label}</div>
                                  <div className="metric-value">
                                    {formatValue(snap[key], key)}
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </main>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics2;
